import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Typography, FormControl, InputLabel, Select, MenuItem, IconButton, Chip, Autocomplete, TablePagination
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import adminService from '../../../services/adminService';
import { formatDate } from '../../../utils/helpers';
import AdminToolbar from '../components/AdminToolbar';

const SubscriptionManagement = () => {
    const [subs, setSubs] = useState([]);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);

    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState({ userId: '', packageId: '', durationDays: 30 });
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({ endDate: '', status: 'ACTIVE' });

    // Options for Create Form
    const [userOptions, setUserOptions] = useState([]);
    const [packageOptions, setPackageOptions] = useState([]);

    useEffect(() => {
        setPage(0);
    }, [search, filterStatus]);

    useEffect(() => {
        fetchSubscriptions();
    }, [search, filterStatus, page, rowsPerPage]);

    const fetchSubscriptions = async () => {
        try {
            const params = {
                page: page + 1,
                limit: rowsPerPage
            };
            if (search) params.search = search;
            if (filterStatus !== 'all') params.status = filterStatus;
            const res = await adminService.getSubscriptions(params);
            if (res.success) {
                setSubs(res.data);
                setTotalRows(res.total || 0);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenCreate = async () => {
        setEditingId(null);
        setFormData({ userId: '', packageId: '', durationDays: 30 });
        setOpenDialog(true);
        // Fetch users and packages for dropdown
        const [usersRes, pkgsRes] = await Promise.all([
            adminService.getAllUsers({}),
            adminService.getAllPackages({ isActive: true })
        ]);
        if (usersRes.success) setUserOptions(usersRes.data);
        if (pkgsRes.success) setPackageOptions(pkgsRes.data);
    };

    const handleOpenEdit = (sub) => {
        setEditingId(sub.id);
        setEditData({
            endDate: sub.endDate.split('T')[0], // Format for date input
            status: sub.status
        });
        setOpenDialog(true);
    };

    const handleSave = async () => {
        try {
            if (editingId) {
                // Update
                await adminService.updateSubscription(editingId, editData);
                toast.success('Cập nhật thành công');
            } else {
                // Create
                if (!formData.userId || !formData.packageId) {
                    toast.error('Vui lòng chọn User và Gói');
                    return;
                }
                await adminService.createSubscription(formData);
                toast.success('Gán gói thành công');
            }
            fetchSubscriptions();
            setOpenDialog(false);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Có lỗi xảy ra');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Xóa đăng ký này? (Hành động xóa vĩnh viễn)')) {
            try {
                await adminService.deleteSubscription(id);
                toast.success('Đã xóa');
                fetchSubscriptions();
            } catch (error) {
                toast.error('Lỗi xóa: ' + error.message);
            }
        }
    };

    return (
        <Box>
            <ToastContainer position="top-right" autoClose={3000} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#f1f5f9', mb: 3 }}>Quản Lý Đăng Ký Gói (Subscriptions)</Typography>

            <AdminToolbar
                searchValue={search}
                onSearchChange={setSearch}
                filterValue={filterStatus}
                onFilterChange={setFilterStatus}
                filterOptions={[
                    { value: 'all', label: 'Tất cả' },
                    { value: 'ACTIVE', label: 'Đang hoạt động' },
                    { value: 'EXPIRED', label: 'Hết hạn' },
                    { value: 'CANCELLED', label: 'Đã hủy' }
                ]}
                onCreate={handleOpenCreate}
                createLabel="Gán Gói Thủ Công"
                placeholder="Tìm user hoặc email..."
            />

            <TableContainer component={Paper} sx={{ bgcolor: '#1e293b', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}>
                <Table>
                    <TableHead sx={{ bgcolor: '#0f172a' }}>
                        <TableRow>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>User</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Gói Dịch Vụ</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Ngày Bắt Đầu</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Ngày Hết Hạn</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Trạng Thái</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Hành Động</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {subs.map((sub) => (
                            <TableRow key={sub.id} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                                <TableCell sx={{ color: 'white' }}>
                                    <Typography fontWeight="bold">{sub.user?.username}</Typography>
                                    <Typography variant="caption" color="#94a3b8">{sub.user?.email}</Typography>
                                </TableCell>
                                <TableCell sx={{ color: '#60a5fa' }}>
                                    {sub.order?.snapshotName || 'Unknown Package'}
                                </TableCell>
                                <TableCell sx={{ color: 'white' }}>{formatDate(sub.startDate)}</TableCell>
                                <TableCell sx={{ color: 'white' }}>{formatDate(sub.endDate)}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={sub.status}
                                        color={sub.status === 'ACTIVE' ? 'success' : 'default'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <IconButton onClick={() => handleOpenEdit(sub)} sx={{ color: '#60a5fa' }}><EditIcon /></IconButton>
                                    <IconButton onClick={() => handleDelete(sub.id)} sx={{ color: '#f87171' }}><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={totalRows}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ color: 'white' }}
            />

            {/* Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingId ? "Cập Nhật Đăng Ký" : "Gán Gói Mới"}</DialogTitle>
                <DialogContent>
                    {!editingId ? (
                        <Box display="flex" flexDirection="column" gap={2} mt={1}>
                            <Autocomplete
                                options={userOptions}
                                getOptionLabel={(option) => `${option.username} (${option.email})`}
                                onChange={(event, newValue) => setFormData({ ...formData, userId: newValue?.id })}
                                renderInput={(params) => <TextField {...params} label="Chọn User" />}
                            />
                            <FormControl fullWidth>
                                <InputLabel>Chọn Gói</InputLabel>
                                <Select
                                    value={formData.packageId}
                                    label="Chọn Gói"
                                    onChange={(e) => setFormData({ ...formData, packageId: e.target.value })}
                                >
                                    {packageOptions.map(pkg => (
                                        <MenuItem key={pkg.id} value={pkg.id}>
                                            {pkg.name} - {Number(pkg.price).toLocaleString()}đ ({pkg.durationDays} ngày)
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Thời Hạn (Ngày) - Để trống dùng mặc định gói"
                                type="number"
                                value={formData.durationDays}
                                onChange={(e) => setFormData({ ...formData, durationDays: e.target.value })}
                            />
                        </Box>
                    ) : (
                        <Box display="flex" flexDirection="column" gap={2} mt={1}>
                            <TextField
                                label="Ngày Hết Hạn"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={editData.endDate}
                                onChange={(e) => setEditData({ ...editData, endDate: e.target.value })}
                            />
                            <FormControl fullWidth>
                                <InputLabel>Trạng Thái</InputLabel>
                                <Select
                                    value={editData.status}
                                    label="Trạng Thái"
                                    onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                                >
                                    <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                                    <MenuItem value="EXPIRED">EXPIRED</MenuItem>
                                    <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Hủy</Button>
                    <Button onClick={handleSave} variant="contained">{editingId ? "Lưu" : "Gán Gói"}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SubscriptionManagement;
